import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MarketingHeader } from '../../components/marketing/MarketingHeader';
import { MarketingFooter } from '../../components/marketing/MarketingFooter';
import { SEOHead } from '../../components/SEO/SEOHead';
import { StructuredData } from '../../components/SEO/StructuredData';
import { createFAQPageSchema, createBreadcrumbListSchema } from '../../components/SEO/StructuredData';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    category: 'Getting Started',
    question: 'What is Monte Carlo simulation?',
    answer: 'Monte Carlo simulation is a technique that runs thousands of scenarios with randomly sampled inputs to show you the range of possible outcomes. Instead of seeing just one result, you see a probability distribution of all possible results.'
  },
  {
    category: 'Getting Started',
    question: 'Do I need to know how to code?',
    answer: 'No! Simcarlo is designed for non-technical users. You can build models by dragging and dropping nodes and connecting them visually. Our AI assistant can even build models from plain English descriptions.'
  },
  {
    category: 'Getting Started',
    question: 'How long does it take to build a model?',
    answer: 'Simple models can be built in minutes. Complex models with many variables might take 30-60 minutes. The AI assistant can speed this up significantly by generating nodes and formulas automatically.'
  },
  {
    category: 'Features',
    question: 'What distribution types are supported?',
    answer: 'We support normal (Gaussian), triangular, uniform, and fixed distributions. Each has configurable parameters like mean, standard deviation, min, max, and mode depending on the type.'
  },
  {
    category: 'Features',
    question: 'How does the AI assistant work?',
    answer: 'The AI assistant interprets natural-language modeling requests and turns them into nodes, formulas, and connections. For example, you can ask it to add a revenue calculation based on price and quantity.'
  },
  {
    category: 'Features',
    question: 'Can I export my results?',
    answer: 'Yes. You can export simulation results to Excel for further analysis and export complete simulation models as JSON.'
  },
  {
    category: 'Collaboration',
    question: 'How does team collaboration work?',
    answer: 'Team workflows are planned. Today, version history helps you preserve model changes while shared editing is still being developed.'
  },
  {
    category: 'Collaboration',
    question: 'Can I share simulations with people outside my team?',
    answer: 'Currently, simulations are private to your account or team workspace. Public sharing and read-only links are coming soon.'
  },
  {
    category: 'Technical',
    question: 'How many trials should I run?',
    answer: 'The right trial count depends on the model and the precision you need. Start with 1,000 trials while shaping the model, then increase the count when reviewing final ranges or sensitive decisions.'
  },
  {
    category: 'Technical',
    question: 'What formula syntax do you support?',
    answer: 'Formulas use JavaScript syntax with basic math operators (+, -, *, /, %) and the math.js library (math.max, math.min, math.sqrt, etc.). Reference other nodes using ${"Node Name"} syntax. The math.js library properly handles units in mathematical functions.'
  },
  {
    category: 'Technical',
    question: 'Can I use my own data as inputs?',
    answer: 'Currently, you define distributions manually. Support for importing data from CSV and fitting distributions automatically is planned for a future release.'
  },
  {
    category: 'Billing',
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, Mastercard, Amex) through Stripe. Enterprise customers can also pay by invoice.'
  },
  {
    category: 'Billing',
    question: 'Can I cancel anytime?',
    answer: 'Yes! You can cancel your subscription at any time. You\'ll retain access until the end of your billing period, and you can always reactivate later.'
  }
];

export const FAQPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const categories = ['All', ...Array.from(new Set(faqData.map(item => item.category)))];
  
  const filteredFAQs = selectedCategory === 'All' 
    ? faqData 
    : faqData.filter(item => item.category === selectedCategory);

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://simcarlo.com';

  return (
    <div className="marketing-page">
      <SEOHead
        metadata={{
          title: 'FAQ - Frequently Asked Questions',
          description: 'Everything you need to know about Simcarlo. Learn about Monte Carlo simulation, features, pricing, billing, and how to get started.',
          keywords: ['FAQ', 'help', 'support', 'Monte Carlo simulation questions', 'how to use Simcarlo'],
          ogImage: `${baseUrl}/images/Landing Main.png`,
          canonicalUrl: `${baseUrl}/faq`,
        }}
      />
      <StructuredData
        schemas={[
          createFAQPageSchema(faqData.map(item => ({
            question: item.question,
            answer: item.answer,
          }))),
          createBreadcrumbListSchema([
            { name: 'Home', url: baseUrl },
            { name: 'FAQ', url: `${baseUrl}/faq` },
          ]),
        ]}
      />
      <MarketingHeader />

      {/* Hero Section */}
      <section className="marketing-hero marketing-faq-hero">
        <div className="marketing-container">
          <div className="marketing-faq-header">
            <h1 className="marketing-section-title">Frequently Asked Questions</h1>
            <p className="marketing-section-description">Everything you need to know about Simcarlo</p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="marketing-faq-section">
        <div className="marketing-container">
          {/* Category Filter */}
          <div className="marketing-faq-categories">
            {categories.map(category => (
              <button
                key={category}
                className={`marketing-faq-category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="marketing-faq-list">
            {filteredFAQs.map((item, index) => (
              <div 
                key={index} 
                className={`marketing-faq-item-expandable ${expandedItems.has(index) ? 'expanded' : ''}`}
              >
                <button
                  className="marketing-faq-question"
                  onClick={() => toggleItem(index)}
                >
                  <span>{item.question}</span>
                  <span className="marketing-faq-icon">{expandedItems.has(index) ? '−' : '+'}</span>
                </button>
                {expandedItems.has(index) && (
                  <div className="marketing-faq-answer">
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="marketing-faq-cta">
        <div className="marketing-container">
          <div className="marketing-cta-content">
            <h2 className="marketing-section-title">Still have questions?</h2>
            <p className="marketing-section-description">We're here to help. Reach out to our team.</p>
            <div className="marketing-cta-buttons">
              <a href="mailto:support@simcarlo.com" className="marketing-btn marketing-btn-primary marketing-btn-large">
                Contact Support
              </a>
              <Link to="/pricing" className="marketing-btn marketing-btn-secondary marketing-btn-large">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};
